import { useId } from 'react';

/**
 * Nexus's face: a warm cream orb inside a violet halo ring, with two plum
 * crescent (smiling) eyes. Used for the centre AI button on the mobile bar
 * and as the general manager's avatar in the agents workspace. Pure SVG so
 * it stays crisp at any size and works on dark and light surfaces.
 */
const CREAM = ['#ffffff', '#f8f4ec', '#e3d9c6'] as const;
const PLUM = '#4a1d6e';
const VIOLET = '#8b5cf6';

function CreamDefs({ body, gloss, shade }: { body: string; gloss: string; shade: string }) {
  return (
    <defs>
      <linearGradient id={body} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={CREAM[0]} />
        <stop offset="60%" stopColor={CREAM[1]} />
        <stop offset="100%" stopColor={CREAM[2]} />
      </linearGradient>
      <linearGradient id={gloss} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
        <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
      </linearGradient>
      <radialGradient id={shade} cx="50%" cy="50%" r="50%">
        <stop offset="78%" stopColor="#2e1065" stopOpacity="0" />
        <stop offset="100%" stopColor="#2e1065" stopOpacity="0.16" />
      </radialGradient>
    </defs>
  );
}

export default function NexusFace({
  size = 40,
  className = '',
  pulse = false,
  title,
}: {
  size?: number;
  className?: string;
  /** Soft breathing animation while Nexus is thinking or connecting. */
  pulse?: boolean;
  title?: string;
}) {
  const id = useId().replace(/:/g, '');
  const body = `nf-body-${id}`;
  const gloss = `nf-gloss-${id}`;
  const shade = `nf-shade-${id}`;
  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={`${pulse ? 'animate-pulse' : ''} ${className}`}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
    >
      {title ? <title>{title}</title> : null}
      <CreamDefs body={body} gloss={gloss} shade={shade} />
      <circle cx="32" cy="32" r="28.5" fill="none" stroke={VIOLET} strokeWidth="3.5" />
      <circle cx="32" cy="32" r="23.5" fill={`url(#${body})`} />
      <circle cx="32" cy="32" r="23.5" fill={`url(#${shade})`} />
      <ellipse cx="25" cy="19" rx="10" ry="4.5" fill={`url(#${gloss})`} />
      <path d="M18 35 Q23.5 26.5 29 35" fill="none" stroke={PLUM} strokeWidth="4" strokeLinecap="round" />
      <path d="M35 35 Q40.5 26.5 46 35" fill="none" stroke={PLUM} strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}

/**
 * The website bot's face: same cream-and-plum family as Nexus, but a rounded
 * square with a teal ring and two round eyes, so the pair reads as siblings
 * rather than twins.
 */
export function WebFace({
  size = 40,
  className = '',
  pulse = false,
  title,
}: {
  size?: number;
  className?: string;
  pulse?: boolean;
  title?: string;
}) {
  const id = useId().replace(/:/g, '');
  const body = `wf-body-${id}`;
  const gloss = `wf-gloss-${id}`;
  const shade = `wf-shade-${id}`;
  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={`${pulse ? 'animate-pulse' : ''} ${className}`}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
    >
      {title ? <title>{title}</title> : null}
      <CreamDefs body={body} gloss={gloss} shade={shade} />
      <rect x="5" y="5" width="54" height="54" rx="17" fill="none" stroke="#14b8a6" strokeWidth="3.5" />
      <rect x="10" y="10" width="44" height="44" rx="13" fill={`url(#${body})`} />
      <rect x="10" y="10" width="44" height="44" rx="13" fill={`url(#${shade})`} />
      <rect x="14" y="13" width="24" height="9" rx="4.5" fill={`url(#${gloss})`} />
      <circle cx="24.5" cy="33" r="4.2" fill={PLUM} />
      <circle cx="39.5" cy="33" r="4.2" fill={PLUM} />
      <circle cx="23.3" cy="31.7" r="1.2" fill="#ffffff" opacity="0.75" />
      <circle cx="38.3" cy="31.7" r="1.2" fill="#ffffff" opacity="0.75" />
    </svg>
  );
}

/**
 * The marketing manager's face: the same cream body and plum eyes, shaped as
 * a speech bubble inside a pink ring, with a wink. It talks to customers, so
 * it looks like it is mid-conversation.
 */
export function MarketingFace({
  size = 40,
  className = '',
  pulse = false,
  title,
}: {
  size?: number;
  className?: string;
  pulse?: boolean;
  title?: string;
}) {
  const id = useId().replace(/:/g, '');
  const body = `mf-body-${id}`;
  const gloss = `mf-gloss-${id}`;
  const shade = `mf-shade-${id}`;
  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={`${pulse ? 'animate-pulse' : ''} ${className}`}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
    >
      {title ? <title>{title}</title> : null}
      <CreamDefs body={body} gloss={gloss} shade={shade} />
      <path d="M8.7 48.4 A28.5 28.5 0 1 1 22.3 58.8 L6 62 Z" fill="none" stroke="#ec4899" strokeWidth="3.5" strokeLinejoin="round" />
      <path d="M12.8 45.5 A23.5 23.5 0 1 1 24 54.1 L11 57.5 Z" fill={`url(#${body})`} />
      <path d="M12.8 45.5 A23.5 23.5 0 1 1 24 54.1 L11 57.5 Z" fill={`url(#${shade})`} />
      <ellipse cx="25" cy="19" rx="10" ry="4.5" fill={`url(#${gloss})`} />
      <path d="M18 34.5 Q23.5 26 29 34.5" fill="none" stroke={PLUM} strokeWidth="4" strokeLinecap="round" />
      <circle cx="40.5" cy="32" r="4.2" fill={PLUM} />
      <circle cx="39.3" cy="30.7" r="1.2" fill="#ffffff" opacity="0.75" />
    </svg>
  );
}

/**
 * The sales manager: the same cream body and plum eyes, shaped as a badge
 * (a rounded shield) inside an emerald ring, with two steady eyes under short
 * brows so it reads as focused rather than chatty. Sits in the same family as
 * Nexus, the web developer, and the marketing manager.
 */
export function SalesFace({
  size = 40,
  className = '',
  pulse = false,
  title,
}: {
  size?: number;
  className?: string;
  pulse?: boolean;
  title?: string;
}) {
  const id = useId().replace(/:/g, '');
  const body = `sf-body-${id}`;
  const gloss = `sf-gloss-${id}`;
  const shade = `sf-shade-${id}`;
  const outer = 'M32 4.5 L55.5 13 V33 C55.5 46.5 45.5 56 32 61 C18.5 56 8.5 46.5 8.5 33 V13 Z';
  const inner = 'M32 10 L50.5 16.8 V33 C50.5 44 42.5 51.5 32 55.6 C21.5 51.5 13.5 44 13.5 33 V16.8 Z';
  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={`${pulse ? 'animate-pulse' : ''} ${className}`}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
    >
      {title ? <title>{title}</title> : null}
      <CreamDefs body={body} gloss={gloss} shade={shade} />
      <path d={outer} fill="none" stroke="#10b981" strokeWidth="3.5" strokeLinejoin="round" />
      <path d={inner} fill={`url(#${body})`} />
      <path d={inner} fill={`url(#${shade})`} />
      <ellipse cx="28" cy="20" rx="10" ry="4.5" fill={`url(#${gloss})`} />
      <path d="M19.5 26.5 L28 28.5" fill="none" stroke={PLUM} strokeWidth="3" strokeLinecap="round" />
      <path d="M44.5 26.5 L36 28.5" fill="none" stroke={PLUM} strokeWidth="3" strokeLinecap="round" />
      <circle cx="24.5" cy="35" r="4.2" fill={PLUM} />
      <circle cx="39.5" cy="35" r="4.2" fill={PLUM} />
      <circle cx="23.3" cy="33.7" r="1.2" fill="#ffffff" opacity="0.75" />
      <circle cx="38.3" cy="33.7" r="1.2" fill="#ffffff" opacity="0.75" />
    </svg>
  );
}

/**
 * Voice sibling of the Nexus face: the same cream orb and violet ring, with
 * plum sound bars instead of eyes, so the two fan options read as a pair.
 */
export function VoiceOrb({
  size = 40,
  className = '',
  pulse = false,
}: {
  size?: number;
  className?: string;
  pulse?: boolean;
}) {
  const id = useId().replace(/:/g, '');
  const body = `vo-body-${id}`;
  const gloss = `vo-gloss-${id}`;
  const shade = `vo-shade-${id}`;
  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={`${pulse ? 'animate-pulse' : ''} ${className}`}
      aria-hidden
    >
      <CreamDefs body={body} gloss={gloss} shade={shade} />
      <circle cx="32" cy="32" r="28.5" fill="none" stroke={VIOLET} strokeWidth="3.5" />
      <circle cx="32" cy="32" r="23.5" fill={`url(#${body})`} />
      <circle cx="32" cy="32" r="23.5" fill={`url(#${shade})`} />
      <ellipse cx="25" cy="19" rx="10" ry="4.5" fill={`url(#${gloss})`} />
      <rect x="20" y="27" width="5" height="11" rx="2.5" fill={PLUM} />
      <rect x="29.5" y="22" width="5" height="21" rx="2.5" fill={PLUM} />
      <rect x="39" y="25" width="5" height="15" rx="2.5" fill={PLUM} />
    </svg>
  );
}
